"use client";

import * as React from "react";
import {
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Brush, Columns3, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePizzaContext } from "@/context/pizza-context";
import { columns } from "../pizzas/columns";
import { VerdictCell } from "@/components/verdict-cell";
import { CheckStrip } from "@/components/check-strip";
import { DateRangeFilter } from "@/components/date-range-filter";
import { formatTime, formatWeekdayDate, shortDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";
import { translate } from "@/i18n/translate";
import { useBranchName } from "@/hooks/use-branch-name";
import { useGetAllPizzaTypes } from "@/api/pizza-checker/pizza-types/pizza-types";
import { PizzaDTO, PizzaType } from "@/api/custom-schema";
import { Fragment, useEffect, useRef } from "react";
import { TableFiltersDialog } from "@/components/table-filters-dialog";
import { PizzaFilters } from "./pizza-filters";
import { ActiveFilters } from "./active-filters";
import { ListPagination } from "@/components/list-pagination";
import { PizzaThumbnail } from "@/components/pizza-thumbnail";
import { useElementWidth } from "@/hooks/use-element-width";
import { useAutoPageSize } from "@/hooks/use-auto-page-size";
import { useLocalStorageState } from "@/hooks/use-local-storage-state";
import { parsePizzaName } from "@/lib/order-items";
import { cn } from "@/lib/utils";

type PizzasProps = {
  getLink: (pizzaId: string) => string;
  /** heading of the list (the summary sits next to it) */
  title?: string;
  /** id of the pizza open in the detail; its row is highlighted */
  cursor?: string;
  rows?: number;
};

// v3: the per-check columns gave way to the "checks" strip (a new key so that the new default applies)
const PIZZA_TABLE_COLUMN_VISIBILITY_KEY = "pizza-table-column-visibility:v3";

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
  photo: true,
  id: false,
  branch_id: false,
  name: false,
  order_id: false,
  capture_at: true,
  capture_count: false,
  "evaluation.metadata.pizza_detection": false,
  "evaluation.metadata.components_bbs": false,
  "evaluation.metadata.overall_evaluation": true,
  checks: true,
  "evaluation.metadata.components_evaluation.base_components": false,
  "evaluation.metadata.components_evaluation.extras": false,
  "evaluation.metadata.components_evaluation.removals": false,
  "evaluation.metadata.confidence": false,
  "evaluation.metadata.component_count": false,
  "evaluation.metadata.doneness_evaluation": false,
  "evaluation.metadata.pizza_saver_evaluation": false,
  "evaluation.metadata.photo_quality_evaluation": false,
  pizza_type: true,
  actions: true,
};

/** Column order of the table: what identifies the pizza, the verdict, then the individual checks. */
const COLUMN_ORDER = [
  "photo",
  "pizza_type",
  "capture_at",
  "branch_id",
  "evaluation.metadata.overall_evaluation",
  "checks",
  "evaluation.metadata.photo_quality_evaluation",
  "evaluation.metadata.components_evaluation.base_components",
  "evaluation.metadata.components_evaluation.extras",
  "evaluation.metadata.components_evaluation.removals",
  "evaluation.metadata.doneness_evaluation",
  "evaluation.metadata.pizza_saver_evaluation",
  "evaluation.metadata.pizza_detection",
  "evaluation.metadata.components_bbs",
  "evaluation.metadata.confidence",
  "evaluation.metadata.component_count",
  "capture_count",
  "name",
  "order_id",
  "id",
  "actions",
];

/** Names of the columns in the visibility menu, in the current language. */
const columnLabels = (): Record<string, string> => ({
  photo: translate("pizzas.columns.photo"),
  id: translate("pizzas.columns.pizzaId"),
  branch_id: translate("common.branch"),
  name: translate("pizzas.columns.fullName"),
  order_id: translate("pizzas.columns.orderId"),
  capture_at: translate("pizzas.columns.capturedAt"),
  capture_count: translate("pizzas.columns.captures"),
  pizza_type: translate("common.pizza"),
  checks: translate("pizzas.columns.checks"),
  "evaluation.metadata.pizza_detection": translate("pizzas.columns.detection"),
  "evaluation.metadata.components_bbs": translate("pizzas.columns.components"),
  "evaluation.metadata.overall_evaluation": translate("pizzas.columns.verdict"),
  "evaluation.metadata.components_evaluation.base_components": translate("pizzas.columns.ingredients"),
  "evaluation.metadata.components_evaluation.extras": translate("pizzas.columns.extras"),
  "evaluation.metadata.components_evaluation.removals": translate("pizzas.columns.removals"),
  "evaluation.metadata.confidence": translate("pizzas.columns.confidence"),
  "evaluation.metadata.component_count": translate("pizzas.columns.componentCount"),
  "evaluation.metadata.doneness_evaluation": translate("pizzas.columns.doneness"),
  "evaluation.metadata.pizza_saver_evaluation": translate("pizzas.columns.pizzaSaver"),
  "evaluation.metadata.photo_quality_evaluation": translate("pizzas.columns.photoQuality"),
});

/** display of a verdict filter value (OK / NOK / NONE) */
const verdictLabel = (value: string) =>
  value === "OK"
    ? translate("status.ok")
    : value === "NOK"
      ? translate("status.nok")
      : value === "NONE"
        ? translate("pizzas.filters.statusNone")
        : value;

/** Below this list width the rows become cards (phones, narrow tablets). */
const CARD_LIST_MAX = 720;

/** clicks on the row's own controls (order link, actions menu) do not open the pizza */
const isRowControl = (target: EventTarget) =>
  target instanceof Element && !!target.closest("button, a, input, [role='menuitem']");

const dayKey = (iso: string | null | undefined) => (iso ? new Date(iso).toDateString() : "");

/** "Today", "Yesterday", or "Thu 15. 10." (with the year when it is not this one). */
function dayLabel(iso: string | null | undefined) {
  if (!iso) return translate("pizzas.list.unknownDay");
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return translate("common.today");
  if (date.toDateString() === yesterday.toDateString()) return translate("common.yesterday");
  return formatWeekdayDate(date);
}

/** "62 pizzas · 31 NOK (50 %)" with a proportion bar, for the current filters. */
function ListSummary({
  total,
  nok,
  verdictFilter,
}: {
  total: number | undefined;
  nok: number | undefined;
  verdictFilter: string;
}) {
  const { t, tp, formatNumber } = useLocalization();
  if (total === undefined) return null;
  const share = nok !== undefined && total > 0 ? nok / total : null;
  return (
    <div className="flex items-center gap-3 text-sm text-muted-foreground">
      <span>
        <span className="font-semibold tabular-nums text-foreground">{formatNumber(total)}</span>{" "}
        {verdictFilter === "ALL"
          ? tp("pizzas.list.unit", total)
          : tp("pizzas.list.verdictUnit", total, { verdict: verdictLabel(verdictFilter) })}
      </span>
      {verdictFilter === "ALL" && share !== null && nok !== undefined && (
        <>
          <span aria-hidden className="text-muted-foreground/40">•</span>
          <span>
            <span className="font-semibold tabular-nums text-red-400">{formatNumber(nok)}</span>{" "}
            {t("status.nok")}{" "}
            <span className="tabular-nums">
              ({formatNumber(share, { style: "percent", maximumFractionDigits: 0 })})
            </span>
          </span>
          <span
            className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-green-500/40 sm:flex"
            title={t("pizzas.list.shareTitle", {
              ok: formatNumber(total - nok),
              nok: formatNumber(nok),
            })}
          >
            <span className="h-full bg-red-500/80" style={{ width: `${share * 100}%` }} />
          </span>
        </>
      )}
    </div>
  );
}

function PizzaCard({
  pizza,
  current,
  timeOnly,
  onOpen,
}: {
  pizza: PizzaDTO;
  current: boolean;
  /** the date is in the day header above */
  timeOnly?: boolean;
  onOpen: () => void;
}) {
  const branchName = useBranchName(pizza.branch_id);
  const { typeId, name } = parsePizzaName(pizza.name);
  return (
    <li data-pizza-row="">
      <button
        type="button"
        onClick={onOpen}
        aria-current={current ? "true" : undefined}
        className={cn(
          "flex w-full items-center gap-3 px-2.5 py-2 text-left transition-colors active:bg-muted",
          current ? "bg-secondary shadow-[inset_3px_0_0_hsl(var(--primary))]" : "hover:bg-muted/50",
        )}
      >
        <PizzaThumbnail
          pizzaId={pizza.id}
          roi={pizza.evaluation?.metadata?.pizza_roi}
          scale={6}
          className="h-14 w-14 shrink-0 rounded-lg"
        />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            {typeId && (
              <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded bg-muted px-1 text-[11px] font-bold tabular-nums">
                {typeId}
              </span>
            )}
            <span className="truncate text-sm font-medium">{name}</span>
          </div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {timeOnly && pizza.capture_at ? formatTime(pizza.capture_at) : shortDateTime(pizza.capture_at)}
            {branchName && ` · ${branchName}`}
          </div>
          <div className="mt-1 flex flex-wrap items-end justify-between gap-x-3 gap-y-1">
            <VerdictCell metadata={pizza.evaluation?.metadata} />
            <CheckStrip metadata={pizza.evaluation?.metadata} size="sm" className="-mr-0.5" />
          </div>
        </div>
      </button>
    </li>
  );
}

// TODO: Finish cursor and rows if provided
export default function Pizzas({ getLink, cursor, title }: PizzasProps) {
  const navigate = useNavigate();
  // texts of the columns, the day headers and the menus follow a language switch
  const { t } = useLocalization();
  const {
    pizzas,
    page,
    setSortBy,
    setSortOrder,
    setPage,
    perPage,
    setPerPage,
    totalPages,
    idFilter,
    setIdFilter,
    orderFilter,
    setOrderFilter,
    captureAtFilterStart,
    captureAtFilterEnd,
    setCaptureAtFilterStart,
    setCaptureAtFilterEnd,
    classificationFilter,
    setClassificationFilter,
    nameFilter,
    setNameFilter,
    confidenceFilter,
    setConfidenceFilter,
    captureCountFilter,
    setCaptureCountFilter,
    componentCountFilter,
    setComponentCountFilter,
    donenessFilter,
    setDonenessFilter,
    totalPizzas,
    pizzaTypeFilter,
    setPizzaTypeFilter,
    overallEvaluationStatusFilter,
    setOverallEvaluationStatusFilter,
    baseComponentsStatusFilter,
    setBaseComponentsStatusFilter,
    extrasStatusFilter,
    setExtrasStatusFilter,
    removalsStatusFilter,
    setRemovalsStatusFilter,
    pizzaSaverStatusFilter,
    setPizzaSaverStatusFilter,
    hasExtrasFilter,
    setHasExtrasFilter,
    hasRemovalsFilter,
    setHasRemovalsFilter,
    photoQualityStatusFilter,
    setPhotoQualityStatusFilter,
    photoQualityCheckFilter,
    setPhotoQualityCheckFilter,
    componentErrorFilter,
    setComponentErrorFilter,
    resetFilters,
    sortBy,
    nokTotal,
  } = usePizzaContext();

  const { data: pizzaTypes } = useGetAllPizzaTypes<PizzaType[]>();
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>(() => {
      const saved = localStorage.getItem(PIZZA_TABLE_COLUMN_VISIBILITY_KEY);
      if (saved) {
        try {
          return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(saved) };
        } catch (e) {
          console.error("Failed to parse column visibility from localStorage", e);
        }
      }
      return DEFAULT_COLUMN_VISIBILITY;
    });
  const [isFilterDialogOpen, setIsFilterDialogOpen] = React.useState(false);

  const [sorting, setSorting] = React.useState<SortingState>([]);

  useEffect(() => {
    if (sorting.length === 0) return;

    setSortBy(sorting.length ? sorting[0].id : "capture_at");
    setSortOrder(sorting.length && sorting[0].desc ? "desc" : "asc");
  }, [sorting]);

  useEffect(() => {
    localStorage.setItem(
      PIZZA_TABLE_COLUMN_VISIBILITY_KEY,
      JSON.stringify(columnVisibility),
    );
  }, [columnVisibility]);

  const [listRef, listWidth] = useElementWidth();
  const table = useReactTable({
    data: pizzas ?? [],
    columns: columns(getLink),
    pageCount: totalPages,
    manualPagination: true,
    onPaginationChange: (updater) => {
      const newPage =
        typeof updater === "function"
          ? updater({ pageIndex: page, pageSize: perPage }).pageIndex
          : updater;
      if (typeof newPage === "number") {
        setPage(newPage);
      }
    },
    manualSorting: true,
    meta: { groupedByDay: sortBy === "capture_at", compactChecks: listWidth > 0 && listWidth < 1000 },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnOrder: COLUMN_ORDER,
      pagination: {
        pageIndex: page,
        pageSize: perPage,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
  });

  const openPizza = (pizzaId: string) => {
    navigate(getLink(pizzaId));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const scrollRef = useRef<HTMLDivElement & HTMLUListElement>(null);
  useEffect(() => {
    const box = scrollRef.current;
    const row = box?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!box || !row) return;
    const boxRect = box.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const headerSpace = 64; // sticky column header + day header
    if (rowRect.top < boxRect.top + headerSpace) {
      box.scrollTop -= boxRect.top + headerSpace - rowRect.top;
    } else if (rowRect.bottom > boxRect.bottom) {
      box.scrollTop += rowRect.bottom - boxRect.bottom + 8;
    }
  }, [cursor, pizzas]);
  const cardList = listWidth > 0 && listWidth < CARD_LIST_MAX;
  // "Auto" page size: a page is as long as the window shows (default), or a fixed number picked below
  const [autoPageSize, setAutoPageSize] = useLocalStorageState(
    "pizza-admin:pizza-list-auto-page-size:v1",
    true,
  );
  useAutoPageSize({
    enabled: autoPageSize,
    boxRef: scrollRef,
    rowSelector: "[data-pizza-row]",
    fallbackRowHeight: cardList ? 92 : 73,
    reserved: cardList ? 28 : 36 + 28,
    perPage,
    setPerPage,
    hasRows: (pizzas?.length ?? 0) > 0,
    layoutKey: `${cardList}:${JSON.stringify(columnVisibility)}`,
  });

  // Calculate active filter count for badge
  const activeFilterCount = [
    idFilter,
    nameFilter,
    orderFilter,
    captureAtFilterStart || captureAtFilterEnd,
    classificationFilter,
    confidenceFilter,
    captureCountFilter,
    componentCountFilter,
    donenessFilter,
    overallEvaluationStatusFilter.length > 0,
    baseComponentsStatusFilter.length > 0,
    extrasStatusFilter.length > 0,
    removalsStatusFilter.length > 0,
    pizzaSaverStatusFilter.length > 0,
    pizzaTypeFilter.length > 0,
    hasExtrasFilter,
    hasRemovalsFilter,
    photoQualityStatusFilter.length > 0,
    photoQualityCheckFilter.length > 0,
    componentErrorFilter,
  ].filter(Boolean).length;

  // quick verdict filter: all / OK only / NOK only (the dialog can still pick both)
  const verdictFilter =
    overallEvaluationStatusFilter.length === 1 ? overallEvaluationStatusFilter[0] : "ALL";
  const rows = table.getRowModel().rows;
  const labels = columnLabels();
  // sorted by capture time: rows are grouped under day headers
  const groupedByDay = sortBy === "capture_at";
  const dayHeader = (index: number) => {
    if (!groupedByDay) return null;
    const day = dayKey(rows[index].original.capture_at);
    return index === 0 || dayKey(rows[index - 1].original.capture_at) !== day
      ? dayLabel(rows[index].original.capture_at)
      : null;
  };

  return (
    <div ref={listRef} className="flex h-full min-h-0 w-full flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h2 className="text-base font-semibold tracking-tight sm:text-lg">{title ?? t("common.pizzas")}</h2>
        <ListSummary total={totalPizzas} nok={nokTotal} verdictFilter={verdictFilter} />
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-0 flex-1 basis-48 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
              placeholder={t("pizzas.toolbar.searchPlaceholder")}
              className="h-9 pl-8 pr-8"
              aria-label={t("pizzas.toolbar.searchLabel")}
            />
            {nameFilter && (
              <button
                type="button"
                onClick={() => setNameFilter("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t("pizzas.toolbar.clearSearch")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div
            role="radiogroup"
            aria-label={t("pizzas.columns.verdict")}
            className="flex h-9 shrink-0 items-center rounded-md border bg-muted/40 p-0.5"
          >
            {[
              { value: "ALL", label: t("common.all") },
              { value: "OK", label: t("status.ok") },
              { value: "NOK", label: t("status.nok") },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={verdictFilter === option.value}
                onClick={() =>
                  setOverallEvaluationStatusFilter(option.value === "ALL" ? [] : [option.value])
                }
                className={cn(
                  "h-full rounded px-3 text-xs font-semibold transition-colors",
                  verdictFilter === option.value
                    ? cn(
                        "bg-background shadow-sm",
                        option.value === "OK"
                          ? "text-green-400"
                          : option.value === "NOK"
                            ? "text-red-400"
                            : "text-foreground",
                      )
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
          <DateRangeFilter
            from={captureAtFilterStart}
            to={captureAtFilterEnd}
            onChange={(from, to) => {
              setCaptureAtFilterStart(from);
              setCaptureAtFilterEnd(to);
            }}
            className="min-w-0 flex-1 basis-40 sm:w-48 sm:flex-none"
          />
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={resetFilters}
              disabled={activeFilterCount === 0}
            >
              <Brush className="h-4 w-4" />
              <span className="hidden sm:inline">{t("common.clear")}</span>
            </Button>
            <TableFiltersDialog
              title={t("pizzas.toolbar.filtersTitle")}
              description={t("pizzas.toolbar.filtersDescription")}
              filterCount={activeFilterCount}
              open={isFilterDialogOpen}
              onOpenChange={setIsFilterDialogOpen}
            >
              <PizzaFilters
                columnVisibility={columnVisibility}
                idFilter={idFilter}
                setIdFilter={setIdFilter}
                nameFilter={nameFilter}
                setNameFilter={setNameFilter}
                orderFilter={orderFilter}
                setOrderFilter={setOrderFilter}
                captureAtFilterStart={captureAtFilterStart}
                captureAtFilterEnd={captureAtFilterEnd}
                setCaptureAtFilterStart={setCaptureAtFilterStart}
                setCaptureAtFilterEnd={setCaptureAtFilterEnd}
                classificationFilter={classificationFilter}
                setClassificationFilter={setClassificationFilter}
                confidenceFilter={confidenceFilter}
                setConfidenceFilter={setConfidenceFilter}
                captureCountFilter={captureCountFilter}
                setCaptureCountFilter={setCaptureCountFilter}
                componentCountFilter={componentCountFilter}
                setComponentCountFilter={setComponentCountFilter}
                donenessFilter={donenessFilter}
                setDonenessFilter={setDonenessFilter}
                overallEvaluationStatusFilter={overallEvaluationStatusFilter}
                setOverallEvaluationStatusFilter={
                  setOverallEvaluationStatusFilter
                }
                baseComponentsStatusFilter={baseComponentsStatusFilter}
                setBaseComponentsStatusFilter={setBaseComponentsStatusFilter}
                extrasStatusFilter={extrasStatusFilter}
                setExtrasStatusFilter={setExtrasStatusFilter}
                removalsStatusFilter={removalsStatusFilter}
                setRemovalsStatusFilter={setRemovalsStatusFilter}
                pizzaSaverStatusFilter={pizzaSaverStatusFilter}
                setPizzaSaverStatusFilter={setPizzaSaverStatusFilter}
                pizzaTypeFilter={pizzaTypeFilter}
                setPizzaTypeFilter={setPizzaTypeFilter}
                pizzaTypes={pizzaTypes}
                hasExtrasFilter={hasExtrasFilter}
                setHasExtrasFilter={setHasExtrasFilter}
                hasRemovalsFilter={hasRemovalsFilter}
                setHasRemovalsFilter={setHasRemovalsFilter}
                photoQualityStatusFilter={photoQualityStatusFilter}
                setPhotoQualityStatusFilter={setPhotoQualityStatusFilter}
                photoQualityCheckFilter={photoQualityCheckFilter}
                setPhotoQualityCheckFilter={setPhotoQualityCheckFilter}
                componentErrorFilter={componentErrorFilter}
                setComponentErrorFilter={setComponentErrorFilter}
              />
            </TableFiltersDialog>
            {!cardList && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Columns3 className="h-4 w-4" />
                    {t("common.columns")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="max-h-[60vh] overflow-y-auto">
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                        onSelect={(event) => event.preventDefault()}
                      >
                        {labels[column.id] ?? column.id}
                      </DropdownMenuCheckboxItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {activeFilterCount > 0 && (
          <ActiveFilters
            idFilter={idFilter}
            setIdFilter={setIdFilter}
            nameFilter={nameFilter}
            setNameFilter={setNameFilter}
            orderFilter={orderFilter}
            setOrderFilter={setOrderFilter}
            captureAtFilterStart={captureAtFilterStart}
            captureAtFilterEnd={captureAtFilterEnd}
            setCaptureAtFilterStart={setCaptureAtFilterStart}
            setCaptureAtFilterEnd={setCaptureAtFilterEnd}
            classificationFilter={classificationFilter}
            setClassificationFilter={setClassificationFilter}
            confidenceFilter={confidenceFilter}
            setConfidenceFilter={setConfidenceFilter}
            captureCountFilter={captureCountFilter}
            setCaptureCountFilter={setCaptureCountFilter}
            componentCountFilter={componentCountFilter}
            setComponentCountFilter={setComponentCountFilter}
            donenessFilter={donenessFilter}
            setDonenessFilter={setDonenessFilter}
            overallEvaluationStatusFilter={overallEvaluationStatusFilter}
            setOverallEvaluationStatusFilter={setOverallEvaluationStatusFilter}
            baseComponentsStatusFilter={baseComponentsStatusFilter}
            setBaseComponentsStatusFilter={setBaseComponentsStatusFilter}
            extrasStatusFilter={extrasStatusFilter}
            setExtrasStatusFilter={setExtrasStatusFilter}
            removalsStatusFilter={removalsStatusFilter}
            setRemovalsStatusFilter={setRemovalsStatusFilter}
            pizzaSaverStatusFilter={pizzaSaverStatusFilter}
            setPizzaSaverStatusFilter={setPizzaSaverStatusFilter}
            pizzaTypeFilter={pizzaTypeFilter}
            setPizzaTypeFilter={setPizzaTypeFilter}
            pizzaTypes={pizzaTypes}
            onFilterClick={() => setIsFilterDialogOpen(true)}
            hasExtrasFilter={hasExtrasFilter}
            setHasExtrasFilter={setHasExtrasFilter}
            hasRemovalsFilter={hasRemovalsFilter}
            setHasRemovalsFilter={setHasRemovalsFilter}
            photoQualityStatusFilter={photoQualityStatusFilter}
            setPhotoQualityStatusFilter={setPhotoQualityStatusFilter}
            photoQualityCheckFilter={photoQualityCheckFilter}
            setPhotoQualityCheckFilter={setPhotoQualityCheckFilter}
            componentErrorFilter={componentErrorFilter}
            setComponentErrorFilter={setComponentErrorFilter}
          />
        )}
      </div>

      {cardList ? (
        <ul ref={scrollRef} className="min-h-0 flex-1 divide-y overflow-auto rounded-lg border">
          {rows.length ? (
            rows.map((row, index) => {
              const header = dayHeader(index);
              return (
                <Fragment key={row.id}>
                  {header && (
                    <li className="sticky top-0 z-10 bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground">
                      {header}
                    </li>
                  )}
                  <PizzaCard
                    pizza={row.original}
                    current={row.original.id === cursor}
                    timeOnly={groupedByDay}
                    onOpen={() => openPizza(row.original.id)}
                  />
                </Fragment>
              );
            })
          ) : (
            <li className="px-4 py-10 text-center text-sm text-muted-foreground">
              {pizzas ? t("pizzas.list.empty") : t("common.loading")}
            </li>
          )}
        </ul>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto rounded-lg border">
          <Table containerClassName="overflow-visible">
            <TableHeader className="sticky top-0 z-20 bg-card shadow-[inset_0_-1px_0_hsl(var(--border))] [&_tr]:border-b-0">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="h-9 whitespace-nowrap">
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {rows.length ? (
                rows.map((row, index) => {
                  const current = row.original.id === cursor;
                  const header = dayHeader(index);
                  return (
                    <Fragment key={row.id}>
                    {header && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={table.getVisibleLeafColumns().length}
                          className="sticky top-9 z-10 bg-secondary px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                        >
                          {header}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      data-pizza-row=""
                      data-state={current ? "selected" : undefined}
                      tabIndex={0}
                      aria-current={current ? "true" : undefined}
                      className={cn(
                        "scroll-mt-16 cursor-pointer outline-none focus-visible:bg-muted/60",
                        current && "bg-secondary shadow-[inset_3px_0_0_hsl(var(--primary))]",
                      )}
                      onClick={(event) => {
                        if (!isRowControl(event.target)) openPizza(row.original.id);
                      }}
                      onKeyDown={(event) => {
                        if (event.target !== event.currentTarget) return;
                        if (event.key === "Enter") {
                          openPizza(row.original.id);
                        } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                          // move between the rows (day headers are skipped)
                          const all = [
                            ...(event.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                              "tr[data-pizza-row]",
                            ) ?? []),
                          ];
                          const next = all[all.indexOf(event.currentTarget) + (event.key === "ArrowDown" ? 1 : -1)];
                          if (next) {
                            event.preventDefault();
                            next.focus();
                          }
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-2">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                    </Fragment>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {pizzas ? t("pizzas.list.empty") : t("common.loading")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        perPage={perPage}
        onPerPageChange={setPerPage}
        total={totalPizzas}
        unit="pizzas"
        autoPageSize={autoPageSize}
        onAutoPageSizeChange={setAutoPageSize}
      />
    </div>
  );
}
